import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button as BsButton,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ColumnGroup } from "primereact/columngroup";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import Select from "react-select";
import * as XLSX from "xlsx";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import nodatafound from "assets/images/no-data.png";
import { GetPurchaseRequisitionSupplierList } from "../../common/data/mastersapi";
import { GetPurchasedReport } from "../../common/data/procurement";



const initFilters = () => ({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  SupplierName: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  GRNDate: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  PONo: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  GRNNo: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  NoInv: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  Description: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
  UoM: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
  },
});

const formatDateToLocal = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatNumber = (val) => {
  if (val === null || val === undefined || isNaN(val)) return "0.00";
  return Number(val).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const getDefaultDates = () => {
  const today = new Date();
  const from = new Date(today);
  const targetMonth = from.getMonth() - 1;
  from.setMonth(targetMonth);
  // Guard against end-of-month overflow (e.g., Oct 31 -> Sep 30)
  if (from.getMonth() === today.getMonth()) {
    from.setDate(0);
  }
  return { from, to: today };
};

const PurchasedReport = () => {
  const dtRef = useRef(null);

  // Filters State - Default to 1 month (1 month ago to today)
  const [fromDate, setFromDate] = useState(() => getDefaultDates().from);
  const [toDate, setToDate] = useState(() => getDefaultDates().to);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [filters, setFilters] = useState(initFilters());
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  // Data & Loading
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load Suppliers list matching Procurement module
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const res = await GetPurchaseRequisitionSupplierList(1, 1, "%");
        if (res?.data?.length > 0) {
          const mapped = res.data.map((item) => ({
            label: item.SupplierName,
            value: item.SupplierID,
          }));
          setSuppliers(mapped);
        }
      } catch (err) {
        console.error("Failed to fetch suppliers:", err);
      }
    };
    fetchSuppliers();
    const defaults = getDefaultDates();
    loadReportData(defaults.from, defaults.to, null);
  }, []);

  // Fetch report data from Python API
  const loadReportData = async (fDate = fromDate, tDate = toDate, sup = selectedSupplier) => {
    setLoading(true);
    const fromStr = fDate ? formatDateToLocal(fDate) : "";
    const toStr = tDate ? formatDateToLocal(tDate) : "";
    const supId = sup?.value || 0;

    try {
      const res = await GetPurchasedReport({
        fromDate: fromStr,
        toDate: toStr,
        supplierId: supId,
      });

      if (res?.status && Array.isArray(res?.data) && res.data.length > 0) {
        setData(
          res.data.map((r, i) => {
            // Display actual Supplier Invoice Number (InvoiceNo / hdr.receiptno) in No Inv column
            const supplierInv =
              r.InvoiceNo && r.InvoiceNo !== "-" && String(r.InvoiceNo).trim() !== ""
                ? r.InvoiceNo
                : (r.NoInv && !String(r.NoInv).toUpperCase().startsWith("IRN")
                  ? r.NoInv
                  : (r.InvoiceNo || "-"));

            return {
              ...r,
              id: r.id || i + 1,
              IRNNo:
                r.IRNNo ||
                (r.NoInv && String(r.NoInv).toUpperCase().startsWith("IRN")
                  ? r.NoInv
                  : "-"),
              NoInv: supplierInv || "-",
            };
          })
        );
      } else {
        setData([]);
      }
    } catch (err) {
      console.error("Failed to load Purchased Report:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const searchData = () => {
    loadReportData(fromDate, toDate, selectedSupplier);
  };

  const cancelFilter = () => {
    const defaults = getDefaultDates();
    setFromDate(defaults.from);
    setToDate(defaults.to);
    setSelectedSupplier(null);
    setFilters(initFilters());
    setGlobalFilterValue("");
    loadReportData(defaults.from, defaults.to, null);
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value || "";
    setFilters((prev) => ({
      ...prev,
      global: { ...prev.global, value },
    }));
    setGlobalFilterValue(value);
  };

  const clearFilter = () => {
    setFilters(initFilters());
    setGlobalFilterValue("");
  };

  // Export to Excel (.xlsx)
  const handleExportExcel = () => {
    const exportRows = data.map((item) => ({
      "SupplierName": item.SupplierName,
      "GRN Date": item.GRNDate,
      "PO No": item.PONo,
      "GRN No": item.GRNNo,
      "No Inv": item.NoInv,
      "Description": item.Description,
      "Qty": Number(item.Qty || 0),
      "UoM": item.UoM,
      "Price": Number(item.Price || 0),
      "Total Price": Number(item.TotalPrice || 0),
    }));

    const totalQty = data.reduce((sum, item) => sum + Number(item.Qty || 0), 0);
    const totalPrice = data.reduce((sum, item) => sum + Number(item.TotalPrice || 0), 0);

    exportRows.push({
      "SupplierName": "GRAND TOTAL",
      "GRN Date": "",
      "PO No": "",
      "GRN No": "",
      "No Inv": "",
      "Description": "",
      "Qty": totalQty,
      "UoM": "",
      "Price": "",
      "Total Price": totalPrice,
    });

    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Purchased Report");
    XLSX.writeFile(wb, `Purchased_Report_${formatDateToLocal(new Date())}.xlsx`);
  };

  const handlePrint = () => {
    window.print();
  };

  // Render header matching InvoiceReceipt and ProcurementsGRN exactly
  const renderHeader = () => {
    return (
      <div className="row align-items-center g-3 clear-spa">
        <div className="col-12 col-lg-6">
          <BsButton className="btn btn-danger btn-label" onClick={clearFilter}>
            <i className="mdi mdi-filter-off label-icon" /> Clear Filter
          </BsButton>
        </div>
        <div className="col-12 col-lg-6 text-end">
          <input
            className="form-control"
            type="text"
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Keyword Search"
            style={{ width: "240px", display: "inline-block" }}
          />
        </div>
      </div>
    );
  };

  // Summary footer calculations
  const totalQty = data.reduce((sum, item) => sum + Number(item.Qty || 0), 0);
  const grandTotalPrice = data.reduce((sum, item) => sum + Number(item.TotalPrice || 0), 0);

  // Footer ColumnGroup pinned at bottom
  const footerGroup = (
    <ColumnGroup>
      <Row>
        <Column
          footer="Grand Total"
          colSpan={6}
          footerStyle={{
            textAlign: "right",
            fontWeight: "bold",
            fontSize: "13px",
            backgroundColor: "#f8f9fa",
          }}
        />
        <Column
          footer={formatNumber(totalQty)}
          footerStyle={{
            textAlign: "right",
            fontWeight: "bold",
            fontSize: "13px",
            backgroundColor: "#f8f9fa",
          }}
        />
        <Column footer="" footerStyle={{ backgroundColor: "#f8f9fa" }} />
        <Column footer="" footerStyle={{ backgroundColor: "#f8f9fa" }} />
        <Column
          footer={formatNumber(grandTotalPrice)}
          footerStyle={{
            textAlign: "right",
            fontWeight: "bold",
            fontSize: "13px",
            color: "#0066a6",
            backgroundColor: "#f8f9fa",
          }}
        />
      </Row>
    </ColumnGroup>
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Procurement" breadcrumbItem="Purchased Report" />

        {/* Top Filters Card - Clean horizontal alignment matching Procurement screens */}
        <Row>
          <Card className="search-top">
            <div className="row align-items-center g-3 quotation-mid p-3">
              {/* GRN From Date */}
              <div className="col-12 col-sm-auto">
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label mb-0 text-nowrap fw-medium">
                    From Date
                  </label>
                  <div style={{ width: "135px" }}>
                    <Flatpickr
                      name="fromDate"
                      className="form-control"
                      value={fromDate || null}
                      onChange={(date) => setFromDate(date?.[0] || null)}
                      placeholder="dd-mmm-yyyy"
                      options={{
                        altInput: true,
                        altFormat: "d-M-Y",
                        dateFormat: "Y-m-d",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* GRN To Date */}
              <div className="col-12 col-sm-auto">
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label mb-0 text-nowrap fw-medium">
                    To Date
                  </label>
                  <div style={{ width: "135px" }}>
                    <Flatpickr
                      name="toDate"
                      className="form-control"
                      value={toDate || null}
                      onChange={(date) => setToDate(date?.[0] || null)}
                      placeholder="dd-mmm-yyyy"
                      options={{
                        altInput: true,
                        altFormat: "d-M-Y",
                        dateFormat: "Y-m-d",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Supplier Filter */}
              <div className="col-12 col-sm-auto">
                <div className="d-flex align-items-center gap-2">
                  <label className="form-label mb-0 text-nowrap fw-medium">
                    Supplier
                  </label>
                  <div style={{ minWidth: "200px" }}>
                    <Select
                      name="supplierSelect"
                      options={suppliers}
                      placeholder="Select Supplier"
                      classNamePrefix="select"
                      isClearable
                      isSearchable
                      value={selectedSupplier}
                      onChange={(selected) => setSelectedSupplier(selected)}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons: Search, Cancel, Export, Print in single aligned row */}
              <div className="col-12 col-lg ms-auto text-end">
                <div className="d-flex justify-content-end align-items-center gap-2 flex-nowrap">
                  <button
                    type="button"
                    className="btn btn-info text-nowrap"
                    onClick={searchData}
                    disabled={loading}
                  >
                    <i className="bx bx-search-alt label-icon font-size-16 align-middle me-2"></i>
                    Search
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger text-nowrap"
                    onClick={cancelFilter}
                  >
                    <i className="bx bx-window-close label-icon font-size-14 align-middle me-2"></i>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success text-nowrap"
                    onClick={handleExportExcel}
                    disabled={data.length === 0}
                  >
                    <i className="bx bx-export label-icon font-size-16 align-middle me-2"></i>
                    Export
                  </button>
                  {/* <button
                    type="button"
                    className="btn btn-secondary text-nowrap"
                    onClick={handlePrint}
                    disabled={data.length === 0}
                  >
                    <i className="bx bx-printer label-icon font-size-16 align-middle me-2"></i>
                    Print
                  </button> */}
                </div>
              </div>
            </div>
          </Card>
        </Row>

        {/* Data Table Card matching Procurement blue-bg and pagination */}
        <Row>
          <Col lg="12">
            <Card className="p-3">
              <DataTable
                ref={dtRef}
                value={data}
                paginator
                showGridlines
                rows={20}
                loading={loading}
                dataKey="id"
                filters={filters}
                globalFilterFields={[
                  "SupplierName",
                  "GRNDate",
                  "PONo",
                  "GRNNo",
                  "NoInv",
                  "Description",
                  "UoM",
                ]}
                header={renderHeader()}
                emptyMessage={
                  <div className="text-center p-4">
                    <img
                      src={nodatafound}
                      alt="No Data"
                      style={{ maxWidth: "47px", marginBottom: "1rem" }}
                    />
                    <div className="font-size-14 fw-bold">No Records Found</div>
                  </div>
                }
                footerColumnGroup={data.length > 0 ? footerGroup : null}
                className="blue-bg"
                onFilter={(e) => setFilters(e.filters)}
              >
                {/* 1. SupplierName */}
                <Column
                  field="SupplierName"
                  header="SupplierName"
                  sortable
                  filter
                  filterPlaceholder="Search by Supplier"
                  className="text-left"
                  style={{ minWidth: "160px" }}
                  body={(row) => row.SupplierName || "-"}
                />

                {/* 2. GRN Date */}
                <Column
                  field="GRNDate"
                  header="GRN Date"
                  sortable
                  filter
                  filterPlaceholder="Search by Date"
                  className="text-center"
                  style={{ minWidth: "110px" }}
                  body={(row) => row.GRNDate || "-"}
                />

                {/* 3. PO No */}
                <Column
                  field="PONo"
                  header="PO No"
                  sortable
                  filter
                  filterPlaceholder="Search by PO"
                  className="text-left"
                  style={{ minWidth: "110px" }}
                  body={(row) => row.PONo || "-"}
                />

                {/* 4. GRN No */}
                <Column
                  field="GRNNo"
                  header="GRN No"
                  sortable
                  filter
                  filterPlaceholder="Search by GRN"
                  className="text-left"
                  style={{ minWidth: "110px" }}
                  body={(row) => row.GRNNo || "-"}
                />

                {/* 5. No Inv */}
                <Column
                  field="NoInv"
                  header="No Inv"
                  sortable
                  filter
                  filterPlaceholder="Search by Inv"
                  className="text-left"
                  style={{ minWidth: "110px" }}
                  body={(row) => row.NoInv || row.InvoiceNo || "-"}
                />

                {/* 6. Description */}
                <Column
                  field="Description"
                  header="Description"
                  sortable
                  filter
                  filterPlaceholder="Search by Description"
                  className="text-left"
                  style={{ minWidth: "250px" }}
                  body={(row) => row.Description || "-"}
                />

                {/* 7. Qty */}
                <Column
                  field="Qty"
                  header="Qty"
                  sortable
                  className="text-end"
                  style={{ minWidth: "80px" }}
                  body={(row) => formatNumber(row.Qty)}
                />

                {/* 8. UoM */}
                <Column
                  field="UoM"
                  header="UoM"
                  sortable
                  filter
                  filterPlaceholder="Search by UoM"
                  className="text-center"
                  style={{ minWidth: "80px" }}
                  body={(row) => row.UoM || "-"}
                />

                {/* 9. Price */}
                <Column
                  field="Price"
                  header="Price"
                  sortable
                  className="text-end"
                  style={{ minWidth: "110px" }}
                  body={(row) => formatNumber(row.Price)}
                />

                {/* 10. Total Price */}
                <Column
                  field="TotalPrice"
                  header="Total Price"
                  sortable
                  className="text-end"
                  style={{ minWidth: "120px", fontWeight: "600" }}
                  body={(row) => formatNumber(row.TotalPrice)}
                />
              </DataTable>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PurchasedReport;
